import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { BsFillGridFill } from "react-icons/bs";
import { FaThList } from "react-icons/fa";
import { AiFillStar } from "react-icons/ai";
import { CiStar } from "react-icons/ci";
import api from "../api/api";
import { getProxiedUrl } from "../utils/imageUtils";
import { getDisplayPrice } from "../utils/priceCalculator";

const GlobalSearch = () => {
  const { searchValue } = useParams();
  const [products, setProducts] = useState([]);
  const [totalProduct, setTotalProduct] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [styles, setStyles] = useState("grid");
  const [sortPrice, setSortPrice] = useState("high-to-low");

  // Set document title
  useEffect(() => {
    document.title = "4POS eCommerce Customer";
  }, []);

  useEffect(() => {
    const fetchSearchResults = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/home/search-products/${encodeURIComponent(searchValue)}`);
        setProducts(data.products || []);
        setTotalProduct(data.totalProduct || 0);
        setError(null);
      } catch (err) {
        console.error("Search error:", err);
        setError("Failed to load search results");
        setProducts([]);
        setTotalProduct(0);
      } finally {
        setLoading(false);
      }
    };

    if (searchValue) {
      fetchSearchResults();
    }
  }, [searchValue]);

  const sortProducts = (products, sortType) => {
    if (!sortType) return products;
    
    return [...products].sort((a, b) => {
      if (sortType === 'low-to-high') {
        return a.price - b.price;
      } else if (sortType === 'high-to-low') {
        return b.price - a.price;
      }
      return 0;
    });
  };

  const sortedProducts = sortProducts(products, sortPrice);

  const NoProductsMessage = () => (
    <div className="w-full bg-white rounded-lg shadow-sm p-8 text-center min-h-[300px] flex items-center justify-center">
      <div>
        <svg 
          className="w-16 h-16 mx-auto mb-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            d="M20 12H4M12 20V4"
          />
        </svg>
        <h2 className="text-2xl font-semibold text-gray-700 mb-3">No Products Found</h2>
        <p className="text-gray-500 mb-4">We couldn't find any products matching "{searchValue}".</p>
        <p className="text-gray-500">Try:</p>
        <ul className="text-gray-500 mt-2">
          <li>• Using different keywords</li>
          <li>• Checking spelling</li>
          <li>• Using more general terms</li>
        </ul>
      </div>
    </div>
  );

  const LoadingSpinner = () => (
    <div className="w-full bg-white rounded-lg shadow-sm p-8 text-center min-h-[300px] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );

  const ErrorMessage = () => (
    <div className="w-full bg-white rounded-lg shadow-sm p-8 text-center min-h-[300px] flex items-center justify-center">
      <div>
        <svg 
          className="w-16 h-16 mx-auto mb-4 text-red-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
        <h2 className="text-2xl font-semibold text-gray-700 mb-3">Search Error</h2>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  const ProductCard = ({ product }) => (
    <div className={`${styles === "grid" ? "w-full" : "w-full flex gap-4"} bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden`}>
      <div className={`${styles === "grid" ? "w-full h-48" : "w-48 h-32"} relative overflow-hidden`}>
        <img
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          src={getProxiedUrl(product.images?.[0])}
          alt={product.name}
          onError={(e) => {
            e.target.src = "/images/no_image.jpg";
          }}
        />
        {product.discount > 0 && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
            -{product.discount}% OFF
          </div>
        )}
      </div>
      
      <div className={`${styles === "grid" ? "p-4" : "flex-1 p-4"} flex flex-col justify-between`}>
        <div>
          {(product.sellerName || product.shopName) && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-gray-500">by</span>
              <Link 
                to={`/${product.sellerName || product.shopName}`}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              >
                {product.shopName || product.sellerName}
              </Link>
            </div>
          )}
          
          <h3 className={`${styles === "grid" ? "text-lg" : "text-xl"} font-semibold text-gray-800 mb-2 line-clamp-2`}>
            {product.name}
          </h3>
          
          <div className="flex items-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <span key={i}>
                {i < Math.floor(product.rating || 0) ? (
                  <AiFillStar className="text-yellow-400" />
                ) : (
                  <CiStar className="text-gray-300" />
                )}
              </span>
            ))}
            <span className="text-sm text-gray-500 ml-1">({product.rating || 0})</span>
          </div>
          
          <div className="flex flex-col gap-0.5 mb-2">
            {(() => {
              const display = getDisplayPrice(product);
              return (
                <>
                  <span className="text-lg font-bold text-indigo-600">
                    {display.currency}{Number(display.price).toFixed(2)}
                    {display.isBulk && (
                      <span className="text-sm font-normal text-gray-600 ml-1">for {display.quantity}</span>
                    )}
                  </span>
                  {display.isBulk && (
                    <span className="text-sm text-gray-500">
                      {display.currency}{Number(display.perItemPrice).toFixed(2)} per item
                    </span>
                  )}
                  {!display.isBulk && product.discount > 0 && (
                    <span className="text-sm text-gray-500 line-through">
                      {display.currency}{Number(product.price || 0).toFixed(2)}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          
          {product.packSize > 1 && (
            <p className="text-sm text-gray-600 mb-2">
              Pack Size: {product.packSize}
            </p>
          )}
        </div>
        
        <div className="flex justify-between items-center">
          <Link
            to={`/${product.sellerName || product.shopName || 'unknown'}/product/details/${product.slug}`}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors duration-200 text-sm font-medium"
          >
            View Details
          </Link>
          
          {(product.sellerName || product.shopName) && (
            <Link
              to={`/${product.sellerName || product.shopName}`}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              Visit Shop
            </Link>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="w-[85%] lg:w-[90%] mx-auto py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="w-[85%] lg:w-[90%] mx-auto py-8">
          <ErrorMessage />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="w-[85%] lg:w-[90%] mx-auto py-6">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Link to="/" className="hover:text-indigo-600">Home</Link>
            <MdOutlineKeyboardArrowRight />
            <span>Search Results</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800">
            Search Results for "{searchValue}"
          </h1>
          <p className="text-gray-600 mt-2">
            Found {totalProduct} product{totalProduct !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="w-[85%] lg:w-[90%] mx-auto py-8">
        {totalProduct > 0 ? (
          <>
            {/* Controls */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-700">
                  {totalProduct} Products Found
                </h2>
                <div className="flex items-center gap-4">
                  <select
                    onChange={(e) => setSortPrice(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={sortPrice}
                  >
                    <option value="high-to-low">High to Low Price</option>
                    <option value="low-to-high">Low to High Price</option>
                  </select>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setStyles("grid")}
                      className={`p-2 rounded ${styles === "grid" ? "bg-indigo-100 text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      <BsFillGridFill />
                    </button>
                    <button
                      onClick={() => setStyles("list")}
                      className={`p-2 rounded ${styles === "list" ? "bg-indigo-100 text-indigo-600" : "text-gray-400 hover:text-gray-600"}`}
                    >
                      <FaThList />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className={`grid gap-6 ${styles === "grid" ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"}`}>
              {sortedProducts.map((product, index) => (
                <ProductCard key={product._id || index} product={product} />
              ))}
            </div>
          </>
        ) : (
          <NoProductsMessage />
        )}
      </div>
    </div>
  );
};

export default GlobalSearch; 